import { initComponent } from './core/init-component';
import { bindAll } from './utils/bind-all';
import { initCursorFollowClose } from './utils/cursor-follow-close';

initComponent('.js-mini-bag', (section) => {
  const overlay = section.querySelector<HTMLElement>('.js-mini-bag-overlay');
  const panel = section.querySelector<HTMLElement>('.js-mini-bag-panel');
  const closeTriggers = section.querySelectorAll<HTMLElement>('.js-mini-bag-close');

  // Close icon that follows the cursor over the overlay (md+).
  const closeFollow = section.querySelector<HTMLElement>('.js-drawer-close-follow');
  const cursorClose = initCursorFollowClose(overlay, closeFollow);
  const header = document.querySelector('.js-header') as HTMLElement | null;  
  const sectionId = section.dataset.sectionId;
  const quantityThrottleMs = Number(section.dataset.quantityThrottleMs ?? 500);
  let isLoading = false;
  let quantityUpdateTimer: number | null = null;

  if (!overlay || !panel || !sectionId) return;  

  const getLine = (trigger: HTMLElement) => trigger.closest<HTMLElement>('.js-mini-bag-line');

  const getLineQuantity = (line: HTMLElement) => Number(line.dataset.quantity);

  const isAtInventoryLimit = (line: HTMLElement) => {
    const inventoryQuantity = line.dataset.inventoryQuantity;
    if (inventoryQuantity === undefined) return false;
    return getLineQuantity(line) >= Number(inventoryQuantity);
  };

  const syncQuantityButtons = () => {
    const lines = section.querySelectorAll<HTMLElement>('.js-mini-bag-line');

    lines.forEach((line) => {
      const minusButton = line.querySelector<HTMLButtonElement>('.js-mini-bag-minus');
      const plusButton = line.querySelector<HTMLButtonElement>('.js-mini-bag-plus');

      if (minusButton) {
        minusButton.disabled = isLoading;
        minusButton.classList.toggle('pointer-events-none', isLoading);
        minusButton.classList.toggle('!cursor-default', isLoading);
      }

      if (plusButton) {
        plusButton.disabled = isLoading || isAtInventoryLimit(line);
        plusButton.classList.toggle('pointer-events-none', isLoading);
        plusButton.classList.toggle('!cursor-default', isLoading);
      }
    });
  };

  if (!window.Shopify?.designMode) {
    // Defer the move to <body> by one task. Moving the panel out of its
    // `.shopify-section` synchronously can happen before sibling components
    // (e.g. the product-grid carousel) have attached their MutationObserver,
    // and initComponent's initial scan only walks `.shopify-section`, so the
    // carousel would never initialise. Deferring lets that scan run first;
    // the later move is then picked up gracefully by the observer.
    setTimeout(() => document.body.appendChild(section), 0);
  }

  const setLoading = (loading: boolean) => {
    isLoading = loading;
  
    const loadingContent = section.querySelectorAll<HTMLElement>('.js-mini-bag-loading');
    const checkout = section.querySelector<HTMLElement>('.js-mini-bag-checkout');
    loadingContent.forEach((content) => {
      content.classList.toggle('opacity-70', loading);
      content.classList.toggle('!cursor-default', loading);
    });

    syncQuantityButtons();
  
    checkout?.classList.toggle('opacity-70', loading);
    checkout?.classList.toggle('pointer-events-none', loading);
  };

  const refreshMiniBag = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
  
    const newInner = doc.querySelector('.js-mini-bag-inner');
    const currentInner = section.querySelector('.js-mini-bag-inner');
  
    if (!newInner || !currentInner) return;

    const miniBagCount = doc.querySelector<HTMLElement>('.js-mini-bag-count');
    const headerCartCounters = document.querySelectorAll<HTMLElement>('.js-header-cart-counter');

    if (miniBagCount) {
      headerCartCounters.forEach((counter) => {
        counter.textContent = miniBagCount.textContent;
      });
    }
  
    currentInner.innerHTML = newInner.innerHTML;
  
    bindEvents();    
  };

  const fetchMiniBag = async () => {
    const response = await fetch(
      `${window.location.pathname}?section_id=${sectionId}`
    );
  
    const html = await response.text();
  
    refreshMiniBag(html);
  };

  const updateQuantity = async (key: string, quantity: number) => {
    if (isLoading) return;
  
    setLoading(true);
  
    try {
      // Prefix with the locale root so the cart request keeps the active market/currency (e.g. /en-eu/).
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: [sectionId],
          sections_url: window.location.pathname,
        }),
      });
  
      const data = await response.json();
      const html = data.sections?.[sectionId];
  
      if (!html) return;
  
      refreshMiniBag(html);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clearQuantityUpdateTimer = () => {
    if (quantityUpdateTimer !== null) window.clearTimeout(quantityUpdateTimer);
    quantityUpdateTimer = null;
  };

  const scheduleQuantityUpdate = (key: string, quantity: number) => {
    clearQuantityUpdateTimer();

    if (quantityThrottleMs <= 0) {
      updateQuantity(key, quantity);
      return;
    }

    quantityUpdateTimer = window.setTimeout(() => {
      quantityUpdateTimer = null;
      updateQuantity(key, quantity);
    }, quantityThrottleMs);
  };

  const openRemoveDialog = (trigger: HTMLElement) => {
    const line = getLine(trigger);
    const dialog = line?.querySelector<HTMLElement>('.js-mini-bag-remove-dialog');

    if (!dialog) return;

    dialog.classList.remove('opacity-0', 'pointer-events-none');
  };

  const setLineQuantity = (line: HTMLElement, quantity: number) => {
    const quantityEl = line.querySelector<HTMLElement>('.js-mini-bag-quantity');

    line.dataset.quantity = String(quantity);
    if (quantityEl) quantityEl.textContent = String(quantity);

    syncQuantityButtons();
  };

  const open = () => {
    overlay.classList.remove('hidden', 'pointer-events-none');
    header?.classList.add('opacity-0', 'pointer-events-none');

    const headerClone = document.querySelector('.js-pdp-header-clone') as HTMLElement | null;
    headerClone?.classList.add('opacity-0', 'pointer-events-none');

    closeTriggers.forEach((trigger) => {
      trigger.classList.remove('hidden');
    });

    requestAnimationFrame(() => {
      overlay.classList.remove('opacity-0');
      panel.classList.remove('translate-x-full');

      document.body.classList.add('overflow-hidden');
    });
  };

  const close = () => {
    overlay.classList.add('opacity-0');
    panel.classList.add('translate-x-full');
    cursorClose.hide();
    header?.classList.remove('opacity-0', 'pointer-events-none');
    
    const headerClone = document.querySelector('.js-pdp-header-clone') as HTMLElement | null;
    headerClone?.classList.remove('opacity-0', 'pointer-events-none');

    document.body.classList.remove('overflow-hidden');

    setTimeout(() => {
      overlay.classList.add('hidden', 'pointer-events-none');

      closeTriggers.forEach((trigger) => {
        trigger.classList.add('hidden');
      });
    }, 400);
  };

  const handleMinusClick = async (event: Event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const line = getLine(button);

    const key = line?.dataset.lineKey;
    const quantity = line ? getLineQuantity(line) : 0;

    if (!line || !key || button.disabled || isLoading) return;

    if (quantity <= 1) {
      clearQuantityUpdateTimer();
      setLineQuantity(line, 0);
      await updateQuantity(key, 0);
      return;
    }

    setLineQuantity(line, quantity - 1);
    scheduleQuantityUpdate(key, quantity - 1);
  };

  const handlePlusClick = async (event: Event) => {
    const button = event.currentTarget as HTMLButtonElement;
    const line = getLine(button);

    const key = line?.dataset.lineKey;
    const quantity = line ? getLineQuantity(line) : 0;

    if (!line || !key || button.disabled || isLoading || isAtInventoryLimit(line)) return;

    const nextQuantity = quantity + 1;

    setLineQuantity(line, nextQuantity);

    if (isAtInventoryLimit(line)) {
      clearQuantityUpdateTimer();
      await updateQuantity(key, nextQuantity);
      return;
    }

    scheduleQuantityUpdate(key, nextQuantity);
  };

  const handleRemoveClick = (event: Event) => {
    const button = event.currentTarget as HTMLElement;

    openRemoveDialog(button);
  };

  const handleCancelRemoveClick = (event: Event) => {
    const button = event.currentTarget as HTMLElement;

    const dialog = button.closest<HTMLElement>('.js-mini-bag-remove-dialog');

    if (!dialog) return;

    dialog.classList.add('opacity-0', 'pointer-events-none');
  };

  const handleConfirmRemoveClick = async (event: Event) => {
    const button = event.currentTarget as HTMLElement;
    const line = getLine(button);
    const key = line?.dataset.lineKey;

    if (!key) return;

    clearQuantityUpdateTimer();
    await updateQuantity(key, 0);
  };

  const handleOpenBagClick = (event: Event) => {
    const trigger = (event.target as HTMLElement).closest('[data-cta-type="open_bag"]');
    if (trigger) open();
  };

  document.addEventListener('click', handleOpenBagClick);

  const bindEvents = () => {
    bindAll(section, '.js-mini-bag-close', 'click', close);
    bindAll(section, '.js-mini-bag-minus', 'click', handleMinusClick);
    bindAll(section, '.js-mini-bag-plus', 'click', handlePlusClick);
    bindAll(section, '.js-mini-bag-remove', 'click', handleRemoveClick);
    bindAll(section, '.js-mini-bag-cancel-remove', 'click', handleCancelRemoveClick);
    bindAll(section, '.js-mini-bag-confirm-remove', 'click', handleConfirmRemoveClick);
    syncQuantityButtons();
  };

  overlay.addEventListener('click', close);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      close();
    }
  });

  window.addEventListener('mini-bag:open', async () => {
    await fetchMiniBag();
    open();
  });

  bindEvents();

  // Open the mini-bag when arriving via the /cart redirect (see layout/theme.liquid), then strip the param.
  const params = new URLSearchParams(window.location.search);
  if (params.has('open-bag')) {
    params.delete('open-bag');
    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
    window.dispatchEvent(new Event('mini-bag:open'));
  }
});
